import { Timestamp } from 'firebase/firestore';

/**
 * Role catalog.
 *
 * - admin   — full access + can invite, remove, and re-role staff
 * - manager — full client/case access, can archive, cannot manage staff or delete
 * - staff   — view/add/edit clients, cases, notes, payments; cannot archive, delete, or manage staff
 */
export type Role = 'admin' | 'manager' | 'staff';

export const ROLES: Role[] = ['admin', 'manager', 'staff'];

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
};

export const ROLE_DESCRIPTION: Record<Role, string> = {
  admin: 'Full access including staff management',
  manager: 'Full client access; cannot manage staff or delete clients',
  staff: 'Client/case access; cannot archive, delete, or manage staff',
};

/**
 * A single payment made to a staff member, recorded by an admin. Entries
 * accumulate into `staffPayTotal` on the StaffUser doc.
 */
export interface StaffPayment {
  id: string;
  amount: number;
  date: string; // ISO yyyy-mm-dd
  note?: string;
  recordedBy?: string; // admin uid who logged it
}

export interface StaffUser {
  id: string; // Firebase Auth uid
  email: string;
  displayName?: string;
  role: Role;
  createdAt: Timestamp;
  invitedBy?: string;
  acceptedInviteId?: string;
  disabled?: boolean;
  /**
   * Running total of everything an admin has paid this staff member. Kept in
   * sync with `staffPayments` (its sum) so the staff Dashboard and admin Staff
   * page can show the total without reading the whole ledger.
   */
  staffPayTotal?: number;
  /** Ledger of individual payments made to this staff member by admins. */
  staffPayments?: StaffPayment[];
}

export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

export interface Invitation {
  id: string; // doc id = invite token (used in URL)
  email: string;
  role: Role;
  status: InvitationStatus;
  createdAt: Timestamp;
  createdBy: string; // admin uid
  createdByEmail?: string;
  expiresAt: Timestamp;
  acceptedAt?: Timestamp;
  acceptedBy?: string;
}

/**
 * Hardcoded bootstrap admin email.
 *
 * When this email signs in for the first time and has no user doc,
 * one is created automatically with role `admin`.
 */
export const BOOTSTRAP_ADMIN_EMAIL = 'kennethdeveloper02@gmail.com';
